import { useState } from "react";
import { api, fmtDateTime, STATUS_LABEL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Download, Trash2, Eye, Loader2, AlertTriangle, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { KIND_LABEL, downloadFile, fmtSize } from "@/lib/files";
import { FileThumb } from "@/components/FileThumb";
import { FileOrderChips } from "@/components/FileOrderChips";

const dl = (f) => downloadFile(f).catch(() => toast.error("Download fehlgeschlagen."));

function VersionList({ f, onPreview }) {
  return (
    <ul className="mt-2 border-l-2 border-slate-200 pl-3 space-y-1.5" data-testid={`kunde-datei-versions-${f.id}`}>
      {f.versions.map((v) => (
        <li key={v.id} className="flex items-center gap-2 flex-wrap text-xs" data-testid={`kunde-datei-version-${v.id}`}>
          <Badge variant="outline" className={`text-xs ${v.version === f.version ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-300"}`}>v{v.version}</Badge>
          <span className="text-slate-700 truncate max-w-[220px]" title={v.original_name}>{v.original_name}</span>
          <span className="text-slate-500">{fmtSize(v.size)} · {fmtDateTime(v.created_at)}</span>
          <FileOrderChips orders={v.orders} testPrefix={`kunde-datei-version-${v.id}`} emptyText="von keinem Auftrag verwendet" />
          <button onClick={() => onPreview(v)} className="text-blue-600 hover:underline focus:outline-none focus-visible:underline" data-testid={`kunde-datei-version-preview-${v.id}`}>Vorschau</button>
          <button onClick={() => dl(v)} className="text-blue-600 hover:underline focus:outline-none focus-visible:underline" data-testid={`kunde-datei-version-download-${v.id}`}>Herunterladen</button>
        </li>
      ))}
    </ul>
  );
}

// Eine Zeile je Datei (Dateifamilie): neueste Version, Versionsanzeige mit aufklappbarer Versionsliste
export function CustomerFileRow({ f, canDelete, onPreview, onDelete }) {
  const [open, setOpen] = useState(false);
  const multi = f.version_count > 1;
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <li className="flex gap-3 p-3 border border-slate-200 rounded-lg bg-white hover:border-slate-300 transition-colors" data-testid={`kunde-datei-${f.id}`}>
      <button onClick={() => onPreview(f)} className="self-start rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label={`Vorschau ${f.original_name}`}>
        <FileThumb att={f} />
      </button>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-baseline gap-2 min-w-0 flex-wrap">
          <button onClick={() => onPreview(f)} className="text-sm font-medium text-slate-900 truncate hover:text-blue-600 text-left focus:outline-none focus-visible:underline" title={f.original_name} data-testid={`kunde-datei-name-${f.id}`}>{f.original_name}</button>
          {multi ? (
            <button onClick={() => setOpen((o) => !o)} aria-expanded={open} className="inline-flex items-center gap-0.5 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-testid={`kunde-datei-version-toggle-${f.id}`}>
              <Chevron className="w-3 h-3" /> v{f.version} · {f.version_count} Versionen
            </button>
          ) : f.version > 1 && <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-xs">v{f.version}</Badge>}
        </div>
        <div className="text-xs text-slate-500" data-testid={`kunde-datei-meta-${f.id}`}>{fmtSize(f.size)} · {f.ext.toUpperCase()} · {KIND_LABEL[f.kind] || f.kind} · {fmtDateTime(f.created_at)}</div>
        <FileOrderChips orders={f.orders} testPrefix={`kunde-datei-${f.id}`} showVersion={multi} />
        {f.orders.some((o) => o.outdated) && <p className="text-xs text-amber-700" data-testid={`kunde-datei-outdated-hint-${f.id}`}>Gelb markierte Aufträge verwenden noch eine ältere Version.</p>}
        {multi && open && <VersionList f={f} onPreview={onPreview} />}
      </div>
      <div className="flex-shrink-0 flex flex-col sm:flex-row gap-1">
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onPreview(f)} title="Vorschau" data-testid={`kunde-datei-preview-${f.id}`}><Eye className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => dl(f)} title="Herunterladen" data-testid={`kunde-datei-download-${f.id}`}><Download className="w-4 h-4" /></Button>
        {canDelete && (
          <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => onDelete(f)} title="In den Papierkorb" data-testid={`kunde-datei-delete-${f.id}`}><Trash2 className="w-4 h-4" /></Button>
        )}
      </div>
    </li>
  );
}

export function DeleteFileDialog({ file, kundeId, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const open = (file?.orders || []).filter((o) => !o.abgeschlossen);
  const n = file?.orders?.length || 0;
  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.delete(`/kunden/${kundeId}/dateien/${file.id}`, { params: open.length ? { force: true } : {} });
      toast.success("Datei in den Papierkorb verschoben – 90 Tage wiederherstellbar.");
      onDeleted();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen."); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={!!file} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="kunde-datei-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Datei löschen?</AlertDialogTitle>
          <AlertDialogDescription data-testid="kunde-datei-delete-text">
            Datei wird in den Papierkorb verschoben (90 Tage wiederherstellbar).
            {` „${file?.original_name || ""}“${file?.version_count > 1 ? ` (alle ${file.version_count} Versionen)` : ""} wird ${n ? `aus ${n === 1 ? "1 Auftrag" : `${n} Aufträgen`} und ` : ""}aus der Kundenliste entfernt.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {open.length > 0 && (
          <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="kunde-datei-delete-warning">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>Achtung: Die Datei ist noch mit nicht abgeschlossenen Aufträgen verknüpft:
              <ul className="mt-1 list-disc pl-5">{open.map((o) => <li key={o.order_id} className="font-mono text-xs">{o.auftragsnummer} – {STATUS_LABEL[o.status] || o.status}</li>)}</ul>
            </div>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="kunde-datei-delete-cancel">Abbrechen</AlertDialogCancel>
          <AlertDialogAction onClick={confirm} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="kunde-datei-delete-confirm">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} In den Papierkorb
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Papierkorb des Kunden: gelöschte Dateien (90 Tage) mit Wiederherstellen (Manager/Admin)
export function TrashRow({ f, kundeId, canRestore, onPreview, onRestored }) {
  const [busy, setBusy] = useState(false);
  const restore = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/kunden/${kundeId}/dateien/papierkorb/${f.trash_id}/wiederherstellen`);
      toast.success(r.data.orders ? `Datei wiederhergestellt – mit ${r.data.orders === 1 ? "1 Auftrag" : `${r.data.orders} Aufträgen`} wieder verknüpft.` : "Datei wiederhergestellt.");
      onRestored();
    } catch (e) { toast.error(e?.response?.data?.detail || "Wiederherstellen fehlgeschlagen."); }
    finally { setBusy(false); }
  };
  return (
    <li className="flex gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50" data-testid={`trash-row-${f.trash_id}`}>
      <FileThumb att={f} />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="text-sm font-medium text-slate-700 truncate" title={f.original_name}>{f.original_name}{f.version_count > 1 ? ` · ${f.version_count} Versionen` : ""}</div>
        <div className="text-xs text-slate-500" data-testid={`trash-meta-${f.trash_id}`}>
          Gelöscht {fmtDateTime(f.deleted_at)}{f.deleted_by_name ? ` von ${f.deleted_by_name}` : ""} · endgültige Löschung am {fmtDateTime(f.purge_at)}
        </div>
        <FileOrderChips orders={f.orders} testPrefix={`trash-${f.trash_id}`} emptyText="war keinem Auftrag zugeordnet" />
      </div>
      <div className="flex-shrink-0 flex flex-col sm:flex-row gap-1 items-start">
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onPreview(f)} title="Vorschau" data-testid={`trash-preview-${f.trash_id}`}><Eye className="w-4 h-4" /></Button>
        {canRestore && (
          <Button size="sm" variant="outline" onClick={restore} disabled={busy} data-testid={`trash-restore-${f.trash_id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />} Wiederherstellen
          </Button>
        )}
      </div>
    </li>
  );
}
